import { CSSProperties, useCallback, useMemo, useState } from "react";
import { UseFormReturn, useFormState, useWatch } from "react-hook-form";
import { classNames } from "primereact/utils";
import styles from "./DesignStudioEntry.module.scss";
import { IStudioWizardFormModel } from "../../models/studioWizardFormModel";
import { IDesignStudioColors } from "../models/designStudioPresetModel";
import {
  designStudioColorKeys,
  pickDesignStudioColors,
} from "../utils/designStudioPaletteUtility";
import {
  buildDesignStudioAccentScope,
  getDesignStudioPaletteName,
} from "../utils/designStudioPreviewUtility";
import { DesignStudioMock } from "./DesignStudioMock";
import { DesignStudioSwatches } from "./DesignStudioSwatches";
import { DesignStudioDialog } from "../DesignStudioDialog";
import { DefaultButton } from "../../../../../../common/components/button/defaultButton/DefaultButton";

interface IDesignStudioEntryProps {
  form: UseFormReturn<IStudioWizardFormModel>;
  studioName?: string;
  width?: string;
}

/** The colour as the form stores it: no '#', no spaces, case kept. */
const clean = (hex: string | null | undefined) => (hex ?? "").trim().replace(/^#/, "");
const bare = (hex: string | null | undefined) => clean(hex).toLowerCase();
const isStorableColor = (hex: string) => /^([A-Fa-f0-9]{6})?$/.test(hex);

export const DESIGN_STUDIO_INVALID_COLOR_MESSAGE =
  "Невалиден цвят. Отворете Design Studio и изберете палитра.";

/** The studio wizard's door to Design Studio: the current palette and a way to change it. */
export const DesignStudioEntry = ({ form, studioName, width }: IDesignStudioEntryProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const watched = useWatch({ control: form.control, name: designStudioColorKeys });
  // Subscribed here, not read from form.formState: this component re-renders on its own
  // when the colours' dirty or error state changes.
  const { dirtyFields, errors } = useFormState({
    control: form.control,
    name: designStudioColorKeys,
  });

  const colors = useMemo(
    () =>
      pickDesignStudioColors(
        Object.fromEntries(designStudioColorKeys.map((key, index) => [key, watched[index]])),
      ),
    [watched],
  );

  const paletteName = getDesignStudioPaletteName(colors);
  // Dirty means "differs from the saved studio": the form loads the saved colours as the
  // fields' default values (useStudioWizardForm).
  const hasUnsavedChanges = designStudioColorKeys.some((key) => !!dirtyFields[key]);
  // The seven colours have no field of their own on the page any more, so a bad one
  // (e.g. a stored "#ffffff") shows here, and the dialog opens where it can be fixed.
  const hasInvalidColor = designStudioColorKeys.some((key) => !!errors[key]);
  const scopeStyle = useMemo(
    () => ({ ...buildDesignStudioAccentScope(colors), width }) as CSSProperties,
    [colors, width],
  );

  const onApply = useCallback(
    (next: IDesignStudioColors) => {
      let changed = false;

      designStudioColorKeys.forEach((key) => {
        const saved = form.formState.defaultValues?.[key] ?? "";
        // The saved spelling of the same colour keeps the field pristine (a stored
        // "8DA9B0" is not changed by a preset's "8da9b0"), unless the saved one is bad.
        const value =
          bare(saved) === bare(next[key]) && isStorableColor(saved) ? saved : clean(next[key]);

        // Only what actually changed: re-applying the same palette is not an edit.
        if (form.getValues(key) !== value) {
          form.setValue(key, value, { shouldDirty: true });
          changed = true;
        }
      });

      // All seven together: the accent pair is checked across fields, so an error on a
      // colour that did not change may be gone now.
      if (changed) {
        void form.trigger(designStudioColorKeys);
      }

      setIsDialogOpen(false);
    },
    [form],
  );

  return (
    <div
      className={classNames(styles.root, { [styles.root_invalid]: hasInvalidColor })}
      style={scopeStyle}
    >
      <div className={styles.preview}>
        <DesignStudioMock colors={colors} variant="card" />
      </div>

      <div className={styles.info}>
        <span className={styles.eyebrow}>Визия на обекта</span>
        <span className={styles.title}>Design Studio</span>

        <span className={styles.palette}>
          <DesignStudioSwatches colors={colors} />
          <span className={styles.palette_name} data-testid="design-studio-entry-name">
            {paletteName}
          </span>
        </span>

        {hasUnsavedChanges && (
          <span className={styles.unsaved} role="status">
            <span className={styles.unsaved_dot} aria-hidden />
            Незапазени промени
          </span>
        )}

        {hasInvalidColor && (
          <span className={styles.error} role="alert">
            {DESIGN_STUDIO_INVALID_COLOR_MESSAGE}
          </span>
        )}

        <span className={styles.cta}>
          <DefaultButton
            label="Отвори Design Studio"
            design="accent"
            width="100%"
            onClick={() => setIsDialogOpen(true)}
          />
        </span>
      </div>

      <DesignStudioDialog
        visible={isDialogOpen}
        initialColors={colors}
        initialMode={hasInvalidColor ? "advanced" : "presets"}
        studioName={studioName}
        onApply={onApply}
        onClose={() => setIsDialogOpen(false)}
      />
    </div>
  );
};
